import { createFileRoute, useNavigate } from '@tanstack/react-router';
import React, { useState, useEffect } from 'react';
import { useStartQuestionnaire, useSaveProgress, useCompleteQuestionnaire } from '../hooks/useApi';
import { storage, isQuestionAnswered, sortQuestionsAndOptions } from '../utils/utils';

import ProgressBar from '../components/ProgressBar';
import QuestionCard from '../components/QuestionCard';
import Navigation from '../components/Navigation';
import { SimpleLoading, CompletionLoading } from '../components/Loading';
import { notificationManager } from '../services/notification.js';
import { firebaseService } from '../services/firebaseService';
import { posthogService } from '../services/posthogService';

export const Route = createFileRoute('/')({
  component: Index,
});

function Index() {
  const navigate = useNavigate();

  // View state
  const [currentView, setCurrentView] = useState('phone');
  const [currentQuestion, setCurrentQuestion] = useState(0);

  // Data state
  const [formData, setFormData] = useState({});
  const [userPhone, setUserPhone] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [questionsData, setQuestionsData] = useState(null);
  const [hasProfile, setHasProfile] = useState(false);

  // UI state
  const [isResuming, setIsResuming] = useState(false);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [answeredQuestions, setAnsweredQuestions] = useState(0);

  // React Query hooks
  const startQuestionnaireMutation = useStartQuestionnaire();
  const saveProgressMutation = useSaveProgress();
  const completeQuestionnaireMutation = useCompleteQuestionnaire();

  // Initialize app - check auth, check for in-progress sessions, and auto-start if needed
  useEffect(() => {
    const checkAndAutoStart = async () => {
      try {
        const currentUser = await firebaseService.getCurrentUser();
        
        if (!currentUser) {
          // Not authenticated, redirect to auth page
          console.log('[Home] Not authenticated, redirecting to /auth');
          navigate({ to: '/auth' });
          return;
        }
        
        // User is already authenticated - check for in-progress sessions
        const phoneNumber = currentUser.phoneNumber;
        const email = currentUser.email;
        
        try {
          const firebaseToken = await firebaseService.getIdToken();
          
          // Check for in-progress sessions first
          const { default: apiService } = await import('../services/apiService');
          const sessions = await apiService.getUserSessions(firebaseToken);
          const inProgressSession = sessions.find(s => s.status === 'processing');
          
          if (inProgressSession) {
            // There's a session being generated, show message
            setCurrentView('generating');
            setSessionId(inProgressSession.session_id);
            return;
          }
          
          // No in-progress session, auto-start the questionnaire
          await handlePhoneVerified(phoneNumber, email, firebaseToken);
        } catch (error) {
          console.error('[Home] Failed to auto-start:', error);
          // If auto-start fails, redirect to auth
          navigate({ to: '/auth' });
        }
      } catch (error) {
        // Auth check failed, redirect to auth
        console.error('[Home] Auth check failed:', error);
        navigate({ to: '/auth' });
      }
    };
    
    checkAndAutoStart();
  }, [navigate]);

  // Keyboard navigation (forward only)
  useEffect(() => {
    const handleKeyDown = e => {
      if (currentView !== 'questionnaire' || !questionsData) return;

      const questions = questionsData.questions;
      const currentQuestionKey = questions[currentQuestion]?.question_key;
      const isAnswered = isQuestionAnswered(formData[currentQuestionKey]);

      if (e.key === 'ArrowRight' && currentQuestion < questions.length - 1 && isAnswered) {
        handleNext();
      } else if (e.key === 'Enter' && isAnswered) {
        e.preventDefault();
        if (currentQuestion === questions.length - 1) {
          handleSubmit();
        } else {
          handleNext();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [currentView, currentQuestion, formData, questionsData]);

  // Track question viewed
  useEffect(() => {
    if (currentView === 'questionnaire' && questionsData) {
      const questions = questionsData.questions;
      const currentQuestionData = questions[currentQuestion];
      if (currentQuestionData) {
        posthogService.questionViewed(
          currentQuestionData.question_key,
          currentQuestion
        );
      }
    }
  }, [currentView, currentQuestion, questionsData]);

  /**
   * Process and sort session data
   */
  const processSessionData = sessionData => {
    return {
      ...sessionData,
      questions: sortQuestionsAndOptions(sessionData.questions || []),
    };
  };

  /**
   * Resume existing session
   */
  const resumeSession = async phoneNumber => {
    try {
      const profile = storage.getUserProfile();
      const rawSessionData = await startQuestionnaireMutation.mutateAsync(phoneNumber, profile || {});
      const sessionData = processSessionData(rawSessionData);
      setQuestionsData(sessionData);
      setHasProfile(sessionData.has_profile || false);

      if (sessionData.is_resuming) {
        setIsResuming(true);
        setTotalQuestions(sessionData.total_questions);
        setAnsweredQuestions(sessionData.total_questions - sessionData.remaining_questions);

        if (sessionData.remaining_questions === 0) {
          // Navigate to results
          storage.clear();
          setTotalQuestions(0);
          setAnsweredQuestions(0);
          setCurrentView('questionnaire');
        }
        setCurrentView('questionnaire');
        setCurrentQuestion(0);
      } else {
        setTotalQuestions(sessionData.total_questions);
        setAnsweredQuestions(0);
        setCurrentView('questionnaire');
      }
    } catch (error) {
      console.error('Error resuming session:', error);
      storage.clear();
      setSessionId('');
      setUserPhone('');
      notificationManager.error(error.message || 'Failed to resume session. Please try again.');
    }
  };

  /**
   * Handle phone verification submission
   */
  const handlePhoneVerified = async (phoneNumber, email, firebaseToken) => {
    setUserPhone(phoneNumber);

    try {
      const profile = storage.getUserProfile();
      const rawSessionData = await startQuestionnaireMutation.mutateAsync(firebaseToken, email, profile || {});
      const sessionData = processSessionData(rawSessionData);
      setQuestionsData(sessionData);
      setHasProfile(sessionData.has_profile || false);

      setSessionId(sessionData.session_id);
      storage.setSessionId(sessionData.session_id);
      storage.setUserPhone(phoneNumber);

      setIsResuming(sessionData.is_resuming);
      setTotalQuestions(sessionData.total_questions);

      // Notify header to re-check auth
      window.dispatchEvent(new Event('auth-changed'));

      // Track questionnaire started
      posthogService.questionnaireStarted(
        sessionData.session_id,
        sessionData.is_resuming,
        sessionData.total_questions
      );

      if (sessionData.is_resuming) {
        setAnsweredQuestions(sessionData.total_questions - sessionData.remaining_questions);

        if (sessionData.remaining_questions === 0) {
          navigate({ to: '/results', search: { sessionId: sessionData.session_id } });
        } else {
          setCurrentView('questionnaire');
          setCurrentQuestion(0);
        }
      } else {
        setAnsweredQuestions(0);
        setCurrentView('questionnaire');
        setCurrentQuestion(0);
      }
    } catch (error) {
      console.error('Error starting questionnaire:', error.message);
      notificationManager.error(error.message || 'Failed to start questionnaire. Please try again.');
    }
  };

  /**
   * Handle form data change
   */
  const handleFormDataChange = (questionKey, value) => {
    setFormData(prev => ({
      ...prev,
      [questionKey]: value,
    }));
    
    // Track question answered
    posthogService.questionAnswered(questionKey, value);
  };

  /**
   * Handle next button
   */
  const handleNext = async () => {
    const questions = questionsData?.questions || [];

    if (currentQuestion < questions.length - 1) {
      if (sessionId && Object.keys(formData).length > 0) {
        try {
          await saveProgressMutation.mutateAsync({
            sessionId,
            responses: formData,
          });
        } catch (error) {
          console.error('Save failed on next:', error);
          notificationManager.error('Failed to save progress. Please try again.');
          return;
        }
      }

      // Track navigation
      posthogService.questionNavigation('next', currentQuestion, currentQuestion + 1);

      setCurrentQuestion(prev => prev + 1);
    }
  };

  /**
   * Handle previous button
   */
  const handlePrev = () => {
    if (currentQuestion > 0) {
      // Track navigation
      posthogService.questionNavigation('previous', currentQuestion, currentQuestion - 1);
      
      setCurrentQuestion(prev => prev - 1);
    }
  };

  /**
   * Handle submit questionnaire
   */
  const handleSubmit = async () => {
    try {
      if (Object.keys(formData).length > 0) {
        await saveProgressMutation.mutateAsync({
          sessionId,
          responses: formData,
        });
      }

      // Track questionnaire completion
      const questions = questionsData?.questions || [];
      posthogService.questionnaireCompleted(sessionId, questions.length);

      await completeQuestionnaireMutation.mutateAsync(sessionId);
      // Navigate directly to analysis page - it will show loading with tips
      navigate({ to: `/analysis/${sessionId}` });
    } catch (error) {
      console.error('Error submitting questionnaire:', error);
      notificationManager.error(error.message || 'Failed to submit questionnaire. Please try again.');
    }
  };

  // Get current question data
  const questions = questionsData?.questions || [];
  const currentQuestionData = questions[currentQuestion];
  const currentQuestionKey = currentQuestionData?.question_key;
  const isCurrentQuestionAnswered = isQuestionAnswered(formData[currentQuestionKey]);

  // Loading states
  const isLoading = startQuestionnaireMutation.isPending;
  const isSubmitting = completeQuestionnaireMutation.isPending;
  const isSaving = saveProgressMutation.isPending;

  return (
    <>
      {/* Generating View - Show when there's a session being generated */}
      {currentView === 'generating' && (
        <div className='bg-white rounded-xl sm:rounded-2xl p-5 sm:p-8 shadow-xl'>
          <div className='max-w-md mx-auto text-center'>
            <div className='text-xs sm:text-sm text-gray-500 mb-2 uppercase tracking-wide'>
              Analysis In Progress
            </div>
            <h2 className='text-xl sm:text-2xl font-light text-gray-900 mb-4 leading-snug'>
              Your Recommendations Are Being Generated
            </h2>
            <p className='text-sm text-gray-600 mb-6 sm:mb-8'>
              Please wait while we analyze your profile and generate personalized credit card recommendations. This process can take a few minutes.
            </p>
            
            <div className='bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8'>
              <div className='flex items-center justify-center gap-2 mb-3'>
                <div className='w-3 h-3 bg-blue-600 rounded-full animate-pulse'></div>
                <span className='text-sm font-medium text-blue-900'>Processing</span>
              </div>
              <p className='text-xs text-blue-700'>
                You can wait here or navigate to the analysis page to see tips about UAE credit cards
              </p>
            </div>

            <button
              onClick={() => navigate({ to: `/analysis/${sessionId}` })}
              className='w-full py-3 sm:py-3.5 bg-gray-900 text-white text-sm sm:text-base font-medium rounded-lg hover:bg-gray-800 active:bg-gray-950 transition-colors mb-3 touch-manipulation min-h-[48px] sm:min-h-[44px]'
            >
              View Analysis Page
            </button>
            
            <button
              onClick={() => navigate({ to: '/recommendations' })}
              className='w-full py-3 sm:py-3.5 bg-white border border-gray-300 text-gray-700 text-sm sm:text-base font-medium rounded-lg hover:bg-gray-50 active:bg-gray-100 transition-colors touch-manipulation min-h-[48px] sm:min-h-[44px]'
            >
              View All Recommendations
            </button>
          </div>
        </div>
      )}

      {/* Questionnaire View */}
      {currentView === 'questionnaire' && (
        <section className='bg-white rounded-xl sm:rounded-2xl p-5 sm:p-8 shadow-xl relative'>
          <ProgressBar
            current={currentQuestion}
            total={isResuming ? totalQuestions : questions.length}
            answered={answeredQuestions}
            isResuming={isResuming}
          />

          {currentQuestionData && (
            <QuestionCard
              question={currentQuestionData}
              formData={formData}
              onChange={handleFormDataChange}
              maxSelections={currentQuestionData.max_selections}
            />
          )}

          <Navigation
            onNext={handleNext}
            onSubmit={handleSubmit}
            showNext={currentQuestion < questions.length - 1}
            showSubmit={currentQuestion === questions.length - 1}
            nextDisabled={!isCurrentQuestionAnswered}
            submitDisabled={!isCurrentQuestionAnswered}
            isSaving={isSaving}
          />
        </section>
      )}
    </>
  );
}
